import React, { useContext, useEffect, useCallback, useState } from 'react';
import SecureLS from 'secure-ls';
import { User } from 'types/User';
import AuthService from 'services/api/AuthService';
import { syncUserpilotState } from 'analytics/userpilot';

const ls = new SecureLS({ encodingType: 'aes' });

type AuthPayload = {
  token: string;
  user: User;
};

export interface AuthContextType {
  login: (auth: AuthPayload) => void;
  logout: () => void;
}

export const AuthContext = React.createContext<AuthContextType>({
  login: () => {},
  logout: () => {},
});

interface AuthContextProviderProps {
  children: React.ReactNode;
}

export const getToken = (): string | undefined => ls.get('token');
export const getUser = (): User | undefined => ls.get('user');

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [session, setSession] = useState<{
    user?: User;
    token?: string;
  }>({
    user: getUser(),
    token: getToken(),
  });

  const user = session.user;
  const token = session.token;

  const login: AuthContextType['login'] = (auth) => {
    ls.set('token', auth?.token);
    ls.set('user', auth?.user);

    setSession({
      user: auth?.user,
      token: auth?.token,
    });

    syncUserpilotState();
  };

  const logout = () => {
    ls.remove('token');
    ls.remove('user');

    setSession({
      user: undefined,
      token: undefined,
    });

    syncUserpilotState();
  };

  const refreshCurrentUser = useCallback(() => {
    if (!token) return;

    AuthService.get_userInfo()
      .then(async (res: any) => {
        if (res.status === 200 || res.status === 201) {
          const refreshedUser = await res.json();

          login({
            user: {
              ...refreshedUser,
              impersonated: user?.impersonated,
            },
            token,
          });
        }
      })
      .catch(() => {});
  }, [token, user?.impersonated]);

  useEffect(() => {
    refreshCurrentUser();
  }, [refreshCurrentUser]);

  useEffect(() => {
    syncUserpilotState();
  }, [user?.id, user?.impersonated]);

  return (
    <AuthContext.Provider value={{ login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
